import React, { PropsWithChildren } from "react";
import { Button, ButtonColors } from "../button/Button.component";
import clsx from "clsx";
import { Translate } from "../text/Text.component";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

export interface CardButton {
    buttonAction: () => void;
    color: ButtonColors;
    text?: string;
    content?: React.ReactElement;
    disabled?: boolean;
}

export interface CardProps {
    cardTitle: string;
    cardType: 'simple' | 'actions' | 'simple-description' | 'description-actions' | 'body-only' | 'simple-double' | 'simple-double-description'
    actions?: CardButton[];
    customActions?: React.ReactNode;
    description?: string;
    translateTitle?: boolean;
    translateDescription?: boolean;
    translateButtons?: boolean;
    hasBody?: boolean;
    titleProps?: React.ComponentPropsWithoutRef<'h3'>;
    descriptionProps?: React.ComponentPropsWithoutRef<'h3'>;
    bodyClass?: string;
    titleClass?: string;
    secondaryDescription?: string;
    secondaryDescriptionProps?: React.ComponentPropsWithoutRef<'h3'>;
    translateSecondaryDescription?: boolean;
    expandable?: boolean;
    defaultExpanded?: boolean;
}

const CardSection = 'bg-surface px-4 py-5 sm:px-6 ';
const CardMain = 'flex flex-col shadow-stone-900/10 dark:shadow-stone-600/15 shadow-lg mb-4 break-words text-gray-900 dark:text-gray-100';

export const Card: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const { cardType } = props;

    if (cardType === 'actions') {
        return <ActionCard {...props}></ActionCard>
    } else if (cardType === 'simple') {
        return <SimpleCard {...props}></SimpleCard>
    } else if (cardType === 'simple-description') {
        return <SimpleDescriptionCard {...props}></SimpleDescriptionCard>
    } else if (cardType === 'description-actions') {
        return <DescriptionActionsCard {...props}></DescriptionActionsCard>
    } else if (cardType === 'body-only') {
        return <BodyOnlyCard {...props}></BodyOnlyCard>
    } else if (cardType === 'simple-double') {
        return <SimpleDoubleCard {...props}></SimpleDoubleCard>
    } else if (cardType === 'simple-double-description') {
        return <SimpleDoubleDescriptionCard {...props}></SimpleDoubleDescriptionCard>
    }

    return (<></>);
}

const ActionCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        actions,
        customActions,
        cardTitle,
        children,
        translateTitle = true,
        translateButtons = true,
        hasBody = true,
        className,
        titleProps,
        bodyClass,
        titleClass,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-2 w-full flex flex-wrap items-center justify-between sm:flex-nowrap break-words">
                    <div className="ml-4 mt-2 w-full md:w-fit flex flex-row justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                    </div>
                    <div className="ml-4 mt-2 flex flex-row items-center gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2">
                        {
                            actions?.map((button: CardButton) => {
                                return <Button
                                    key={button.text || button.color}
                                    onClick={button.buttonAction}
                                    color={button.color}
                                    disabled={button.disabled || false}
                                >
                                    {
                                        button.text ?
                                            (
                                                translateButtons ?
                                                    <Translate translation_key={button.text} /> :
                                                    <> {button.text} </>
                                            ) : (
                                                button.content ? button.content : null
                                            )
                                    }
                                </Button>
                            })
                        }

                        {customActions}

                        {expandable &&
                            <div
                                className="ml-4 mb-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const SimpleDoubleCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        cardTitle,
        children,
        translateTitle = true,
        hasBody = true,
        className,
        titleProps,
        description,
        translateDescription = true,
        bodyClass,
        titleClass,
        descriptionProps,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-2 w-full flex flex-wrap items-center justify-between sm:flex-nowrap break-words">
                    <div className="ml-4 mt-2 w-full md:w-fit flex flex-row justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                    </div>
                    <div className="ml-4 mt-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2">
                        {
                            description !== '' ?
                                <h3 className={clsx("card-title", descriptionProps?.className)}>
                                    {
                                        translateDescription ?
                                            <Translate translation_key={description || ''}></Translate> :
                                            <> {description} </>
                                    }
                                </h3> : null
                        }

                        {expandable &&
                            <div
                                className="ml-4 mb-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const SimpleDoubleDescriptionCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        cardTitle,
        children,
        translateTitle = true,
        hasBody = true,
        className,
        titleProps,
        description,
        translateDescription = true,
        bodyClass,
        titleClass,
        descriptionProps,
        secondaryDescription,
        secondaryDescriptionProps,
        translateSecondaryDescription = true,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-2 w-full flex flex-wrap items-center justify-between sm:flex-nowrap break-words">
                    <div className="ml-4 mt-2 w-[calc(75%_-_16px)] md:w-fit flex flex-col justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                        {
                            description !== '' ?
                                <h4 className={clsx("card-description", descriptionProps?.className)}>
                                    {
                                        translateDescription ?
                                            <Translate translation_key={description || ''}></Translate> :
                                            <> {description} </>
                                    }
                                </h4> : null
                        }
                    </div>
                    <div className="ml-4 flex flex-row gap-x-3 flex-shrink-0 w-[calc(25%_-_16px)] md:w-fit justify-center pt-2">
                        {
                            secondaryDescription !== '' ?
                                <h3 className={clsx("card-title", secondaryDescriptionProps?.className)}>
                                    {
                                        translateSecondaryDescription ?
                                            <Translate translation_key={secondaryDescription || ''}></Translate> :
                                            <> {secondaryDescription} </>
                                    }
                                </h3> : null
                        }

                        {expandable &&
                            <div
                                className="ml-4 mb-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const SimpleCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        cardTitle,
        children,
        translateTitle = true,
        hasBody = true,
        className,
        titleProps,
        bodyClass,
        titleClass,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-2 w-full flex flex-wrap items-center justify-between sm:flex-nowrap break-words">
                    <div className="ml-4 mt-2 w-[calc(75%_-_16px)] md:w-fit flex flex-row justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                    </div>
                    {expandable &&
                        <div className="ml-4 flex flex-row gap-x-3 flex-shrink-0 w-[calc(25%_-_16px)] md:w-fit justify-center pt-2">
                            <div
                                className="ml-4 mb-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const SimpleDescriptionCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        cardTitle,
        description,
        children,
        translateTitle = true,
        translateDescription = true,
        hasBody = true,
        className,
        titleProps,
        bodyClass,
        titleClass,
        descriptionProps,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-2 w-full flex flex-wrap items-center justify-between sm:flex-nowrap break-words">
                    <div className="ml-4 mt-2 w-[calc(75%_-_16px)] md:w-fit flex flex-col justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                        {
                            description !== '' ?
                                <h3 className={clsx("card-description", descriptionProps?.className)}>
                                    {
                                        translateDescription ?
                                            <Translate translation_key={description || ''}></Translate> :
                                            <> {description} </>
                                    }
                                </h3> : null
                        }
                    </div>
                    {expandable &&
                        <div className="ml-4 flex flex-row gap-x-3 flex-shrink-0 w-[calc(25%_-_16px)] md:w-fit justify-center pt-2">
                            <div
                                className="ml-4 mt-2 flex flex-row gap-x-3 flex-shrink-0 w-full md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const DescriptionActionsCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const {
        actions,
        cardTitle,
        description,
        children,
        translateTitle = true,
        translateDescription = true,
        translateButtons = true,
        hasBody = true,
        className,
        descriptionProps,
        titleProps,
        bodyClass,
        titleClass,
        expandable = false,
        defaultExpanded = true
    } = props;

    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(titleClass ? titleClass + 'px-4 py-5 sm:px-6' : CardSection, hasBody && isExpanded ? 'border-b border-surface-border rounded-t-lg' : 'rounded-lg')}>
                <div className="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div className="ml-4 mt-2 w-[calc(75%_-_16px)] md:w-fit flex flex-col justify-center">
                        {
                            cardTitle !== '' ?
                                <h3 className={clsx("card-title", titleProps?.className)}>
                                    {
                                        translateTitle ?
                                            <Translate translation_key={cardTitle}></Translate> :
                                            <> {cardTitle} </>
                                    }
                                </h3> : null
                        }
                        {
                            description !== '' ?
                                <h3 className={clsx("card-description", descriptionProps?.className)}>
                                    {
                                        translateDescription ?
                                            <Translate translation_key={description || ''}></Translate> :
                                            <> {description} </>
                                    }
                                </h3> : null
                        }
                    </div>
                    <div className="ml-4 mt-4 flex-shrink-0 flex mr-4 gap-4">
                        {
                            actions?.map((button: CardButton) => {
                                return <Button
                                    key={button.text || button.color}
                                    onClick={button.buttonAction}
                                    color={button.color}
                                    disabled={button.disabled || false}
                                >
                                    {
                                        button.text ?
                                            (
                                                translateButtons ?
                                                    <Translate translation_key={button.text} /> :
                                                    <> {button.text} </>
                                            ) : (
                                                button.content ? button.content : null
                                            )
                                    }
                                </Button>
                            })
                        }

                        {expandable &&
                            <div
                                className="ml-4 flex flex-row gap-x-3 flex-shrink-0 w-[calc(25%_-_16px)] md:w-fit justify-center pt-2"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {!isExpanded &&
                                    <ChevronDownIcon className="w-5 h-5" />
                                }
                                {isExpanded &&
                                    <ChevronUpIcon className="w-5 h-5" />
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
            {
                hasBody && isExpanded ?
                    <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-b-lg')}>
                        {children}
                    </div> : null
            }
        </div>
    )
}

const BodyOnlyCard: React.FunctionComponent<PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>> = (
    props: PropsWithChildren<CardProps> & React.ComponentPropsWithoutRef<'div'>
): JSX.Element => {

    const { children, className, bodyClass } = props;

    return (
        <div className={clsx(CardMain, className)}>
            <div className={clsx(bodyClass ? bodyClass + 'px-4 py-5 sm:px-6' : CardSection, 'rounded-lg')}>
                {children}
            </div>
        </div>
    )
}